import { Box, useClassNames } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import LeaveItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/LeaveItem'
import WrapperCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/WrapperCell'
import DayCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/DayCell'
import * as stylesRefs from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/index.vanilla.css'
import {
  type ColumnsType,
  dailyGridTemplateColumns,
  gridColumn,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'

import { leaveFirstCell, leaveLastCell } from './utils'

interface Props {
  shiftsRow: ShiftsRow
  columns: ColumnsType
  visibleDefaultShifts: DefaultShifts
}

const WeeklyRow = ({ shiftsRow, columns, visibleDefaultShifts }: Props) => {
  const [{ view }] = useSchedule()

  const { viewOptions } = useCurrentViewOptions()
  const {
    schedule: { showCompactView },
  } = viewOptions
  const firstDay = view.firstDay
  const lastDay = view.lastDay

  const cx = useClassNames(stylesRefs)

  const { leaves } = shiftsRow

  const disabledCells = columns.filter((c) => shiftsRow.isDisabled(view, c.date))
  const leavesToRender = leaves.leaves
    .filter((leave) => {
      return !disabledCells.some((c) =>
        c.date.isBetween(leave._startOn, leave._finishOn, 'day', '[]')
      )
    })
    .sort((a, b) => b.duration() - a.duration())

  // Leaves are position absolute, so we need to push the EmployeeDayCell down when
  // such are present in the current day/cell.
  // We can have multiple leaves per day, so we need to multiply the offset by the amount
  // of leaves present.
  const leaveOffset = ({ count }: { count: number }) => {
    const base = showCompactView ? 40 : 52
    return `${base * count}px`
  }

  return (
    <WrapperCell minWidth="full">
      <Box
        className={cx('grid')}
        style={{ ...dailyGridTemplateColumns(view), height: '100%', position: 'relative' }}
      >
        {!!leavesToRender.length && (
          <Box
            className={cx('grid')}
            style={{
              ...gridColumn(0, 7),
              ...dailyGridTemplateColumns(view),
              position: 'absolute',
              width: '100%',
              zIndex: '-1',
            }}
          >
            {leavesToRender.map((leave) => (
              <Box
                key={leave.id}
                paddingX="s4"
                paddingTop="s4"
                style={{
                  ...gridColumn(leaveFirstCell(leave, firstDay), leaveLastCell(leave, lastDay)),
                }}
              >
                <LeaveItem
                  leave={leave}
                  shiftsRow={shiftsRow}
                  date={leave._startOn}
                  span={leaveLastCell(leave, lastDay) - leaveFirstCell(leave, firstDay)}
                />
              </Box>
            ))}
          </Box>
        )}

        {columns.map((c) => {
          const leavesForDay = leavesToRender.filter((leave) => {
            const endDate = leave._finishOn || view.lastDay
            return c.date.isBetween(leave._startOn, endDate, 'day', '[]')
          })
          const hasLeave = !!leavesForDay.length

          return (
            <Box
              key={`DayCell-${shiftsRow.key}-${c.formattedDate}`}
              style={{
                ...gridColumn(moment(c.date).weekday(), moment(c.date).weekday() + 1),
                marginTop: hasLeave ? leaveOffset({ count: leavesForDay.length }) : '0',
              }}
            >
              <DayCell
                date={c.date}
                shiftsRow={shiftsRow}
                visibleDefaultShifts={visibleDefaultShifts}
              />
            </Box>
          )
        })}
      </Box>
    </WrapperCell>
  )
}

export default observer(WeeklyRow)
